/**
 * One row of segments in the combined model picker (Reasoning, Speed or
 * Access), as a radio group. Left and Right move between its segments. A
 * segment the model lacks folds to a zero-width track while the others ease
 * out to fill the row.
 *
 * @module components/chat/ModelEffortSegmented
 */
import type { KeyboardEvent, ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Tip } from "../ui/tip";

export interface Segment {
  readonly id: string;
  readonly label: string;
  readonly title: string;
  readonly icon?: ReactNode;
  readonly absent?: boolean;
}

const SEGMENT =
  "relative flex h-6 min-w-0 cursor-pointer items-center justify-center gap-1 rounded-md px-1.5 whitespace-nowrap text-muted-foreground text-xs outline-none hover:text-foreground aria-checked:bg-input/72 aria-checked:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50";

/** Arrow keys move between the radios of one group; nothing outside sees them. */
function onRadioGroupKeyDown(event: KeyboardEvent<HTMLElement>) {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.stopPropagation();
  const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=radio]:not(:disabled)"));
  const index = items.findIndex((item) => item === document.activeElement);
  const next = items[index + (event.key === "ArrowRight" ? 1 : -1)];
  if (!next) return;
  event.preventDefault();
  next.focus();
}

export function ModelEffortSegmented({
  label,
  segments,
  value,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly segments: ReadonlyArray<Segment>;
  readonly value: string | null;
  readonly disabled: boolean;
  readonly onChange: (id: string) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid w-full rounded-lg bg-input/40 p-0.5 transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none"
      style={{ gridTemplateColumns: segments.map((segment) => (segment.absent ? "minmax(0,0fr)" : "minmax(0,1fr)")).join(" ") }}
      onKeyDown={onRadioGroupKeyDown}
    >
      {segments.map((segment) => {
        const checked = segment.id === value;
        const absent = segment.absent ?? false;
        // Keyed by label, not option id: ids differ between models, and a remount would pop the icon in late.
        return (
          <Tip key={segment.label} label={segment.title}>
            <button
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={segment.label}
              data-effort-option=""
              tabIndex={checked && !absent ? 0 : -1}
              aria-hidden={absent || undefined}
              inert={absent}
              disabled={disabled}
              className={cn(SEGMENT, "overflow-hidden transition-[opacity,padding] duration-300 ease-in-out", absent && "pointer-events-none px-0 opacity-0")}
              onClick={() => onChange(segment.id)}
            >
              {segment.icon}
              {segment.label}
            </button>
          </Tip>
        );
      })}
    </div>
  );
}
